import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import "./report.scss";
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCol,
  MDBContainer,
  MDBInput,
  MDBRow,
} from "mdb-react-ui-kit";
import agent from "../../../agent";
import { LOADER_SHOW } from "../../../constants/actionTypes";

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = (dispatch) => ({
  callLoaderShow: (type) => dispatch({ type: LOADER_SHOW, payload: { type } }),
});

function toISODate(input) {
  if (!input) return "";
  return String(input).slice(0, 10);
}

function parseWebsites(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(String(raw));
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(Boolean)
      .map((w) => ({
        key: String(w.key || ""),
        name: w.name || "",
        ga4PropertyId: w.ga4PropertyId || "",
        gscSiteUrl: w.gscSiteUrl || "",
      }))
      .filter((w) => w.key);
  } catch {
    return [];
  }
}

const GoogleReports = (props) => {
  const { callLoaderShow } = props;

  const [errorMsg, setErrorMsg] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [websiteKey, setWebsiteKey] = useState("");

  const [startDate, setStartDate] = useState(toISODate(new Date(Date.now() - 5 * 86400000).toISOString()));
  const [endDate, setEndDate] = useState(toISODate(new Date().toISOString()));

  const [compareOn, setCompareOn] = useState(false);
  const [compareStartDate, setCompareStartDate] = useState(
    toISODate(new Date(Date.now() - 60 * 86400000).toISOString())
  );
  const [compareEndDate, setCompareEndDate] = useState(
    toISODate(new Date(Date.now() - 31 * 86400000).toISOString())
  );

  const [acq, setAcq] = useState(null);
  const [events, setEvents] = useState(null);
  const [landing, setLanding] = useState(null);

  // Per-report loading states so each card shows a spinner independently
  const [acqLoading, setAcqLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [landingLoading, setLandingLoading] = useState(false);

  const didAutoLoadRef = useRef(false);

  const selectedWebsite = useMemo(() => {
    return websites.find((w) => w.key === websiteKey) || null;
  }, [websites, websiteKey]);

  const loadWebsites = async () => {
    try {
      callLoaderShow(true);
      const r = await agent.Auth.getSettings();
      callLoaderShow(false);
      const data = r && r.data && r.data.settings ? r.data.settings : null;
      const list = parseWebsites(data ? data.google_websites : null);
      setWebsites(list);
      if (!websiteKey && list.length > 0) setWebsiteKey(list[0].key);
    } catch (err) {
      callLoaderShow(false);
      setErrorMsg(err?.message || "Failed to load Google websites from settings.");
    }
  };

  useEffect(() => {
    loadWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load on first valid selection (client request)
  useEffect(() => {
    if (!websiteKey || !startDate || !endDate) return;
    if (didAutoLoadRef.current) return;
    didAutoLoadRef.current = true;
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteKey, startDate, endDate]);

  const loadReports = async () => {
    setErrorMsg(null);
    setAcq(null);
    setEvents(null);
    setLanding(null);

    if (!websiteKey) {
      setErrorMsg("Please select a website.");
      return;
    }
    if (!startDate || !endDate) {
      setErrorMsg("Please select date range.");
      return;
    }

    // Guard against excessively large date ranges that cause 502 timeouts
    const daysDiff = (new Date(endDate) - new Date(startDate)) / 86400000;
    if (daysDiff > 180) {
      setErrorMsg("Date range is too large (max 180 days). Please narrow your selection.");
      return;
    }

    const params = {
      startDate,
      endDate,
      limit: 100,
    };
    if (compareOn && compareStartDate && compareEndDate) {
      params.compareStartDate = compareStartDate;
      params.compareEndDate = compareEndDate;
    }

    // Fire all 3 requests in parallel but update each card independently as they resolve
    setAcqLoading(true);
    setEventsLoading(true);
    setLandingLoading(true);

    agent.Google.gaUserAcquisition(websiteKey, params)
      .then((r) => setAcq(r?.data?.report || r?.report || null))
      .catch((err) => setErrorMsg(err?.message || "Failed to load user acquisition."))
      .finally(() => setAcqLoading(false));

    agent.Google.gaEvents(websiteKey, params)
      .then((r) => setEvents(r?.data?.report || r?.report || null))
      .catch((err) => setErrorMsg(err?.message || "Failed to load events."))
      .finally(() => setEventsLoading(false));

    agent.Google.gaLandingPages(websiteKey, params)
      .then((r) => setLanding(r?.data?.report || r?.report || null))
      .catch((err) => setErrorMsg(err?.message || "Failed to load landing pages."))
      .finally(() => setLandingLoading(false));
  };

  const renderRows = (report) => {
    const rows = report && report.rows ? report.rows : [];
    const dimensionHeaders = report && report.dimensionHeaders ? report.dimensionHeaders : [];
    const metricHeaders = report && report.metricHeaders ? report.metricHeaders : [];
    if (!rows.length) return <div className="text-muted">No data.</div>;

    return (
      <div className="table-responsive">
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              {dimensionHeaders.map((h) => (
                <th key={h.name}>{h.name}</th>
              ))}
              {metricHeaders.map((h) => (
                <th key={h.name}>{h.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                {(r.dimensionValues || []).map((v, i) => (
                  <td key={`d_${i}`}>{v.value}</td>
                ))}
                {(r.metricValues || []).map((v, i) => (
                  <td key={`m_${i}`}>{v.value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPreviewRows = (report, detailPath, isLoading) => {
    if (isLoading) {
      return (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
          <span className="text-muted">Loading...</span>
        </div>
      );
    }

    const preview = {
      ...report,
      rows: (report?.rows || []).slice(0, 3),
    };

    const query = new URLSearchParams({
      websiteKey: websiteKey || "",
      startDate: startDate || "",
      endDate: endDate || "",
      ...(compareOn && compareStartDate && compareEndDate
        ? {
            compareStartDate,
            compareEndDate,
          }
        : {}),
    }).toString();

    return (
      <Fragment>
        {renderRows(preview)}
        <div className="d-flex justify-content-end mt-3">
          <Link to={`${detailPath}?${query}`} className="btn btn-link text-decoration-none">
            View details
          </Link>
        </div>
      </Fragment>
    );
  };

  return (
    <MDBContainer className="mt-5">
      <MDBCard className="mb-4">
        <MDBCardHeader>
          <h4 className="mb-0">GA / GSC Reports</h4>
        </MDBCardHeader>
        <MDBCardBody>
          {errorMsg ? <div className="alert alert-danger">{errorMsg}</div> : <Fragment />}

          <MDBRow className="g-3 align-items-start">
            <MDBCol className="col-lg-4 col-xl-3 col-xxl-4 col-md-12 col-sm-12">
              <label className="form-label">Website</label>
              <select
                className="form-select"
                value={websiteKey}
                onChange={(e) => setWebsiteKey(e.target.value)}
              >
                <option value="">Select website</option>
                {websites.map((w) => (
                  <option key={w.key} value={w.key}>
                    {w.name || w.key}
                  </option>
                ))}
              </select>
              {selectedWebsite ? (
                <div className="text-muted small mt-1">
                  GA4: {selectedWebsite.ga4PropertyId || "-"} | GSC: {selectedWebsite.gscSiteUrl || "-"}
                </div>
              ) : null}
            </MDBCol>
            <MDBCol className="col-lg-4 col-xl-3 col-md-12 col-sm-12">
              <label className="form-label">Start date</label>
              <MDBInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </MDBCol>
            <MDBCol className="col-lg-4 col-xl-3 col-md-12 col-sm-12">
              <label className="form-label">End date</label>
              <MDBInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </MDBCol>
            <MDBCol className="col-lg-12 col-xl-3 col-sm-12 col-xxl-2 d-flex align-items-center gap-1">
              <MDBBtn className="text-sm px-2 btn-custom w-100" color="primary" onClick={loadReports}>
                Load
              </MDBBtn>
              <MDBBtn className="text-sm px-2 btn-custom w-100" color="secondary" onClick={loadWebsites}>
                Refresh
              </MDBBtn>
            </MDBCol>
          </MDBRow>

         
        </MDBCardBody>
      </MDBCard>

      <MDBCard className="mb-4">
        <MDBCardHeader>
          <h5 className="mb-0">User acquisition</h5>
        </MDBCardHeader>
        <MDBCardBody>{renderPreviewRows(acq, "/google-reports/user-acquisition", acqLoading)}</MDBCardBody>
      </MDBCard>

      <MDBCard className="mb-4">
        <MDBCardHeader>
          <h5 className="mb-0">Events</h5>
        </MDBCardHeader>
        <MDBCardBody>{renderPreviewRows(events, "/google-reports/events", eventsLoading)}</MDBCardBody>
      </MDBCard>

      <MDBCard className="mb-4">
        <MDBCardHeader>
          <h5 className="mb-0">Landing pages</h5>
        </MDBCardHeader>
        <MDBCardBody>{renderPreviewRows(landing, "/google-reports/landing-pages", landingLoading)}</MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(GoogleReports);